import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/shop/CartContext";

const LINKS = [
  { to: "/shop", label: "Home", end: true },
  { to: "/shop/all", label: "Shop" },
  { to: "/shop/collections", label: "Collections" },
  { to: "/shop/about", label: "About" },
  { to: "/shop/contact", label: "Contact" },
  { to: "/shop/track", label: "Track Order" },
];

export default function SignalNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (l) => (l.end ? pathname === l.to : pathname.startsWith(l.to));

  return (
    <header
      data-testid="signal-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/shop" className="flex items-center gap-2.5 group" data-testid="signal-logo">
          <span className="w-2.5 h-2.5 rounded-full bg-sh-blue shadow-[0_0_16px_#0a84ff] group-hover:scale-125 transition-transform" />
          <span className="font-display font-semibold tracking-tight text-[17px]">Signal House</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`signal-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-[13px] tracking-tight transition-colors ${
                isActive(l) ? "text-white" : "text-white/55 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCartOpen(true)}
            className="relative p-2.5 text-white/80 hover:text-white transition-colors"
            aria-label="Open cart"
            data-testid="signal-cart-button"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.6} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-sh-blue text-white text-[10px] font-semibold flex items-center justify-center" data-testid="signal-cart-count">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2.5 text-white" aria-label="Menu" data-testid="signal-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10" data-testid="signal-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base text-white/80 hover:text-white">
                {l.label}
              </Link>
            ))}
            <Link to="/" onClick={() => setOpen(false)} className="text-xs text-white/40 mt-2">← Back to James 1:1</Link>
          </div>
        </div>
      )}
    </header>
  );
}
